import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/SEOHead';

const brands = [
  {
    id: 'speedy',
    name: 'Speedy DMV Services™',
    tagline: 'Fast. Friendly. Reliable.',
    description:
      'Speedy DMV Services™ was built on one simple promise: get clients in and out faster than any government DMV office — without sacrificing accuracy or care. Since opening, Speedy DMV has become a trusted name in the Las Vegas Valley for vehicle registration, title transfers, and all things DMV.',
    mission:
      'To eliminate the frustration of DMV bureaucracy by providing fast, accurate, and friendly service that respects every client\'s time.',
    accentColor: 'text-emerald-700',
    bgAccent: 'bg-emerald-50',
    borderAccent: 'border-emerald-200',
    badgeBg: 'bg-emerald-100',
    services: [
      'Vehicle Registration & Renewal',
      'Title Transfers',
      'License Plate Tags',
      'Same-Day Processing',
      'Out-of-State Transfers',
      'Duplicate Titles',
    ],
    highlights: [
      { icon: 'ri-timer-flash-line', label: 'Same-Day Service', value: 'Available' },
      { icon: 'ri-user-smile-line', label: 'Client Satisfaction', value: '4.9 / 5' },
      { icon: 'ri-map-pin-2-line', label: 'Service Area', value: 'Clark County' },
      { icon: 'ri-calendar-check-line', label: 'Years Operating', value: '15+' },
    ],
    heroImage:
      'https://public.readdy.ai/ai/img_res/f0c691bfc70e56177f2e934649b0ff87.jpg',
    galleryImages: [
      'https://readdy.ai/api/search-image?query=Professional%20customer%20service%20representative%20at%20a%20clean%20modern%20desk%20reviewing%20vehicle%20registration%20documents%20with%20a%20client%2C%20organized%20office%20environment%2C%20warm%20lighting%2C%20friendly%20interaction&width=400&height=280&seq=brand-speedy-g1&orientation=landscape',
      'https://readdy.ai/api/search-image?query=Organized%20filing%20system%20with%20vehicle%20title%20documents%20and%20registration%20papers%20in%20a%20professional%20office%2C%20clean%20and%20efficient%20workspace%2C%20neutral%20background&width=400&height=280&seq=brand-speedy-g2&orientation=landscape',
      'https://readdy.ai/api/search-image?query=Happy%20customer%20receiving%20completed%20vehicle%20registration%20documents%20from%20a%20professional%20staff%20member%2C%20bright%20office%20setting%2C%20satisfied%20client%20interaction%2C%20Las%20Vegas%20DMV%20services&width=400&height=280&seq=brand-speedy-g3&orientation=landscape',
    ],
  },
  {
    id: 'xpress',
    name: 'DMV-Xpress Services™',
    tagline: 'Streamlined. Thorough. Client-First.',
    description:
      'DMV-Xpress Services™ specializes in the more complex side of DMV transactions — from VIN inspections and document preparation to salvage title processing and concierge support. Where other services stop, DMV-Xpress goes further.',
    mission:
      'To handle the most complex DMV and document challenges with precision, professionalism, and a personal touch that makes every client feel supported.',
    accentColor: 'text-amber-700',
    bgAccent: 'bg-amber-50',
    borderAccent: 'border-amber-200',
    badgeBg: 'bg-amber-100',
    services: [
      'VIN Inspections',
      'Document Preparation',
      'Smog Check Guidance',
      'Salvage Title Processing',
      'Concierge Support',
      'Corporate Fleet Accounts',
    ],
    highlights: [
      { icon: 'ri-search-eye-line', label: 'VIN Inspections', value: 'Certified' },
      { icon: 'ri-file-shield-2-line', label: 'Document Accuracy', value: '99.8%' },
      { icon: 'ri-building-2-line', label: 'Fleet Clients', value: '50+' },
      { icon: 'ri-translate-2', label: 'Languages', value: 'Multilingual' },
    ],
    heroImage:
      'https://readdy.ai/api/search-image?query=Professional%20document%20processing%20center%20with%20organized%20filing%20systems%20and%20modern%20computers%2C%20staff%20carefully%20reviewing%20vehicle%20paperwork%20and%20titles%2C%20efficient%20clean%20office%20environment%2C%20amber%20warm%20lighting%2C%20Las%20Vegas%20Nevada%20business&width=800&height=500&seq=brand-xpress-hero&orientation=landscape',
    galleryImages: [
      'https://readdy.ai/api/search-image?query=Certified%20vehicle%20inspector%20examining%20a%20car%20VIN%20number%20with%20official%20clipboard%20and%20documentation%2C%20professional%20inspection%20process%2C%20clean%20organized%20environment%2C%20good%20lighting&width=400&height=280&seq=brand-xpress-g1&orientation=landscape',
      'https://readdy.ai/api/search-image?query=Professional%20office%20worker%20carefully%20completing%20official%20DMV%20forms%20and%20documents%20at%20a%20clean%20organized%20desk%2C%20focused%20and%20precise%20work%2C%20warm%20office%20lighting&width=400&height=280&seq=brand-xpress-g2&orientation=landscape',
      'https://readdy.ai/api/search-image?query=Corporate%20fleet%20of%20vehicles%20parked%20in%20an%20organized%20lot%20with%20a%20professional%20manager%20reviewing%20documents%2C%20business%20fleet%20management%2C%20clean%20professional%20setting&width=400&height=280&seq=brand-xpress-g3&orientation=landscape',
    ],
  },
];

export default function BrandsPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${siteUrl}/brands#webpage`,
        url: `${siteUrl}/brands`,
        name: 'Our Brands | Speedy DMV Services & DMV-Xpress Services | Villarreal Enterprise Group',
        description: 'Meet the two trusted brands under Villarreal Enterprise Group: Speedy DMV Services™ for fast vehicle registration and DMV-Xpress Services™ for complex document and VIN inspection needs in Las Vegas.',
        isPartOf: { '@id': `${siteUrl}/#website` },
        breadcrumb: { '@id': `${siteUrl}/brands#breadcrumb` },
        dateModified: '2026-04-11',
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${siteUrl}/brands#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Our Brands', item: `${siteUrl}/brands` },
        ],
      },
      {
        '@type': 'Brand',
        '@id': `${siteUrl}/#brand-speedy`,
        name: 'Speedy DMV Services™',
        description: 'Fast, friendly, and reliable DMV services in Las Vegas — vehicle registration, title transfers, and same-day processing for Clark County residents.',
        url: siteUrl,
        logo: 'https://static.readdy.ai/image/1aff587802347e8a3b9191c90eecded3/c67bf49259dce1e180324b942fef6f74.png',
        slogan: 'Fast. Friendly. Reliable.',
      },
      {
        '@type': 'Brand',
        '@id': `${siteUrl}/#brand-xpress`,
        name: 'DMV-Xpress Services™',
        description: 'Specialized DMV services including VIN inspections, document preparation, salvage title processing, and corporate fleet accounts in Las Vegas, Nevada.',
        url: siteUrl,
        logo: 'https://static.readdy.ai/image/1aff587802347e8a3b9191c90eecded3/c67bf49259dce1e180324b942fef6f74.png',
        slogan: 'Streamlined. Thorough. Client-First.',
      },
      {
        '@type': 'ItemList',
        name: 'DMV Service Brands',
        description: 'The two brands operated by Villarreal Enterprise Group in Las Vegas, Nevada.',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Speedy DMV Services™',
            url: siteUrl,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'DMV-Xpress Services™',
            url: siteUrl,
          },
        ],
      },
    ],
  };

  return (
    <>
      <SEOHead
        title="Our Brands | Speedy DMV Services & DMV-Xpress Services | Villarreal Enterprise Group"
        description="Meet the two trusted brands under Villarreal Enterprise Group: Speedy DMV Services™ for fast vehicle registration and DMV-Xpress Services™ for complex document and VIN inspection needs in Las Vegas."
        keywords="Speedy DMV Services Las Vegas, DMV-Xpress Services Nevada, DMV brands Las Vegas, vehicle registration companies Nevada"
        canonical={`${siteUrl}/brands`}
        schema={schema}
        schemaId="schema-brands"
      />
      <Header />

      {/* Hero */}
      <section className="relative bg-gray-900 py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Two%20professional%20business%20storefronts%20side%20by%20side%20on%20a%20clean%20commercial%20street%2C%20modern%20signage%2C%20welcoming%20entrances%2C%20Las%20Vegas%20Nevada%20business%20district%2C%20daytime%20with%20clear%20sky%2C%20professional%20corporate%20aesthetic&width=1400&height=500&seq=brands-hero-001&orientation=landscape"
            alt="Our Brands"
            className="w-full h-full object-cover object-top opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900 via-gray-900/85 to-gray-900/60"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-sm text-gray-400 mb-6">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white">Our Brands</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Our Companies
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl leading-relaxed">
            Two distinct brands. One shared commitment to excellence. Both proudly operated under 
            Villarreal Enterprise Group in Las Vegas, Nevada.
          </p>
        </div>
      </section>

      {/* Parent Company Note */}
      <section className="bg-gray-50 border-b border-gray-200 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
            <div className="w-10 h-10 flex items-center justify-center shrink-0">
              <i className="ri-building-4-line text-2xl text-gray-500"></i>
            </div>
            <p className="text-gray-600 text-sm">
              <strong className="text-gray-900">Villarreal Enterprise Group</strong> is the parent company of both brands below, 
              operated by <strong className="text-gray-900">The Villarreal Organization, Inc</strong>. 
              Each brand operates independently with its own team, focus, and client base.
            </p>
          </div>
        </div>
      </section>

      {/* Brand Sections */}
      {brands.map((brand, index) => (
        <section
          key={brand.id}
          className={`py-24 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Brand Header */}
            <div className={`flex flex-col lg:flex-row gap-16 items-center ${index % 2 !== 0 ? 'lg:flex-row-reverse' : ''}`}>
              <div className="flex-1">
                <div className={`inline-flex items-center gap-2 ${brand.badgeBg} rounded-full px-4 py-2 mb-6`}>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`ri-store-2-line ${brand.accentColor} text-sm`}></i>
                  </div>
                  <span className={`text-sm font-semibold ${brand.accentColor}`}>{brand.tagline}</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">{brand.name}</h2>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">{brand.description}</p>
                <div className={`${brand.bgAccent} ${brand.borderAccent} border rounded-lg p-4 mb-8`}>
                  <p className="text-sm font-semibold text-gray-700 mb-1">Our Mission</p>
                  <p className={`text-sm ${brand.accentColor} leading-relaxed italic`}>{brand.mission}</p>
                </div>

                {/* Highlights */}
                <div className="grid grid-cols-2 gap-4 mb-8">
                  {brand.highlights.map((h, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-lg shrink-0">
                        <i className={`${h.icon} text-lg text-gray-700`}></i>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">{h.label}</div>
                        <div className="font-semibold text-gray-900 text-sm">{h.value}</div>
                      </div>
                    </div>
                  ))}
                </div>

                <Link
                  to="/#contact"
                  className="inline-flex items-center gap-2 bg-gray-900 text-white px-6 py-3 rounded-lg font-medium hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-mail-send-line"></i>
                  Contact This Brand
                </Link>
              </div>

              <div className="flex-1 w-full">
                <img
                  src={brand.heroImage}
                  alt={brand.name}
                  className="w-full h-[380px] object-cover object-top rounded-xl mb-4"
                />
                <div className="grid grid-cols-3 gap-3">
                  {brand.galleryImages.map((img, i) => (
                    <img
                      key={i}
                      src={img}
                      alt={`${brand.name} gallery ${i + 1}`}
                      className="w-full h-24 object-cover object-top rounded-lg"
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Services List */}
            <div className="mt-16">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Services Offered</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {brand.services.map((svc, i) => (
                  <div
                    key={i}
                    className={`${brand.bgAccent} ${brand.borderAccent} border rounded-lg p-4 text-center`}
                  >
                    <p className={`text-sm font-medium ${brand.accentColor}`}>{svc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Comparison */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-bold mb-4">Which Brand Is Right for You?</h2>
            <p className="text-gray-400">Both brands are excellent — here's how to choose.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white/10 rounded-xl p-8 border border-white/10">
              <h3 className="text-xl font-bold text-emerald-400 mb-4">Choose Speedy DMV if you need…</h3>
              <ul className="space-y-3">
                {[
                  'Fast vehicle registration or renewal',
                  'A straightforward title transfer',
                  'Same-day or walk-in service',
                  'License plate tags or stickers',
                  'Out-of-state vehicle registration',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-300 text-sm">
                    <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                      <i className="ri-checkbox-circle-line text-emerald-400"></i>
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white/10 rounded-xl p-8 border border-white/10">
              <h3 className="text-xl font-bold text-amber-400 mb-4">Choose DMV-Xpress if you need…</h3>
              <ul className="space-y-3">
                {[
                  'A certified VIN inspection',
                  'Complex document preparation',
                  'Salvage or rebuilt title processing',
                  'Smog check guidance or waivers',
                  'Corporate or fleet account services',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-300 text-sm">
                    <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                      <i className="ri-checkbox-circle-line text-amber-400"></i>
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="text-center mt-10">
            <p className="text-gray-400 mb-6 text-sm">Not sure? Contact us and we'll point you in the right direction.</p>
            <Link
              to="/#contact"
              className="inline-flex items-center gap-2 bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-question-answer-line"></i>
              Ask Us Anything
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
